import { supabase } from '../supabase';

// Telemetria + persistencia del onboarding v2 (planes 1.4 y 1.5).
// TODO lo de este archivo es best-effort: nunca lanza ni bloquea el flujo del
// usuario. Si falla la red/RLS, se traga el error y sigue. La UX no depende de
// que estas escrituras salgan bien.

const isBrowser = () => typeof window !== 'undefined';

/**
 * Registra un hito del embudo de activacion en onboarding_events (F8 / 1.5).
 * Eventos esperados: signup, provisioned, tour_started, tour_step_completed,
 * tour_paused, tour_abandoned, tour_completed, first_real_sale,
 * checklist_step_done, checklist_completed.
 */
// La RLS de onboarding_events exige `user_id = auth.uid()` o la organizacion
// del usuario. Varios call sites (el cartel de un tour antes de que cargue el
// perfil, por ejemplo) mandaban los dos en null: el insert se rechazaba con 403
// y quedaba a la vista en la consola. Resolvemos el usuario de la sesion como
// respaldo y, si no hay ninguno de los dos, ni se intenta.
let cachedAuthUserId = null;

const resolveAuthUserId = async () => {
  if (cachedAuthUserId) return cachedAuthUserId;
  try {
    const { data } = await supabase.auth.getUser();
    cachedAuthUserId = data?.user?.id || null;
  } catch {
    cachedAuthUserId = null;
  }
  return cachedAuthUserId;
};

export async function logOnboardingEvent({
  event,
  stepId = null,
  organizationId = null,
  userId = null,
  metadata = null,
} = {}) {
  if (!event) return;
  try {
    const resolvedUserId = userId || (await resolveAuthUserId());
    // Sin usuario ni organizacion el insert lo rechaza la RLS: se descarta el
    // evento en vez de ensuciar la consola con un 403 por cada paso.
    if (!resolvedUserId && !organizationId) return;

    // `insert` no lanza: devuelve { error }. Antes el catch nunca se ejecutaba
    // y el fallo pasaba desapercibido salvo por el 403 en la pestaña de red.
    const { error } = await supabase.from('onboarding_events').insert([{
      organization_id: organizationId,
      user_id: resolvedUserId,
      event,
      step_id: stepId,
      metadata: metadata && typeof metadata === 'object' ? metadata : {},
    }]);
    if (error && isBrowser()) {
      console.debug('[onboardingTelemetry] logOnboardingEvent skip:', error.message);
    }
  } catch (error) {
    // Silencioso a proposito: la instrumentacion no debe afectar la operatoria.
    if (isBrowser()) console.debug('[onboardingTelemetry] logOnboardingEvent skip:', error?.message);
  }
}

// Version "dispara y olvida" para call sites sincronicos (no esperar la promesa).
export function trackOnboardingEvent(payload) {
  void logOnboardingEvent(payload);
}

/**
 * Espeja el estado de un recorrido en onboarding_progress (F3 / 1.4), por
 * usuario. localStorage sigue siendo el cache; esto es la fuente para hidratar
 * al cambiar de dispositivo o navegador.
 */
export async function saveOnboardingProgress({
  userId,
  organizationId = null,
  key = 'first_sale',
  state = {},
} = {}) {
  if (!userId) return;
  try {
    await supabase.from('onboarding_progress').upsert([{
      user_id: userId,
      organization_id: organizationId,
      key,
      state: state && typeof state === 'object' ? state : {},
      updated_at: new Date().toISOString(),
    }], { onConflict: 'user_id,key' });
  } catch (error) {
    if (isBrowser()) console.debug('[onboardingTelemetry] saveOnboardingProgress skip:', error?.message);
  }
}

export function persistOnboardingProgress(payload) {
  void saveOnboardingProgress(payload);
}

/**
 * Lee el estado persistido de un recorrido para hidratar el cache local.
 * Devuelve el objeto `state` guardado o null.
 */
export async function loadOnboardingProgress({ userId, key = 'first_sale' } = {}) {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from('onboarding_progress')
      .select('state')
      .eq('user_id', userId)
      .eq('key', key)
      .maybeSingle();
    if (error) return null;
    return data?.state || null;
  } catch {
    return null;
  }
}
